import React from "react";
import { useRouter } from "next/router";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField
} from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { yupTrimToNull } from "../../../../utils/stringUtils";

import { IAdminUser as Entity, IAdminUserUpdateReq as SaveReq } from "../types";
import { paths } from "../utils";

const useStyles = makeStyles({
  button: {
    paddingTop: "16px"
  },
  layoutButton: {
    flex: "1 1 auto"
  },
  skeleton: {
    display: "inline-flex",
    height: "72px",
    width: "100%"
  }
});

interface Props {
  isLoading: boolean;
  isSaving: boolean;
  data?: Entity;
  onSubmit: (req: SaveReq) => void;
}

type States = SaveReq;

const schema = yup.object().shape({
  username: yup.string().trim().required("กรุณาระบุ Username"),
  fullName: yup.string().trim().required("กรุณาระบุ ชื่อ"),
  email: yup.string().transform(yupTrimToNull),
  active: yup.boolean()
});

const CreateForm: React.FC<Props> = props => {
  const { isLoading, isSaving, data, onSubmit } = props;
  const classes = useStyles();
  const router = useRouter();

  const defaultFormData = React.useMemo(
    () => ({
      username: data?.username,
      fullName: data?.fullName,
      email: data?.email,
      active: data?.active ?? true
    }),
    [data]
  );

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<States>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  React.useEffect(() => {
    if (data) {
      reset(defaultFormData);
    }
  }, [data, defaultFormData, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="username"
              control={control}
              defaultValue={defaultFormData.username}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.username}
                  helperText={errors.username?.message}
                  {...field}
                  label="Username *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="fullName"
              control={control}
              defaultValue={defaultFormData.fullName}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.fullName}
                  helperText={errors.fullName?.message}
                  {...field}
                  label="ชื่อ *"
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <Controller
              name="email"
              control={control}
              defaultValue={defaultFormData.email}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  fullWidth
                  disabled={isSaving}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  {...field}
                  label="Email (สำหรับส่งเมลแจ้งเตือนจากระบบ)"
                />
              )}
            />
          )}
        </Grid>

        <Grid item>
          {isLoading ? (
            <Skeleton className={classes.skeleton} />
          ) : (
            <FormGroup row>
              <FormControlLabel
                control={
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={defaultFormData.active}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        checked={value}
                        onChange={onChange}
                        disabled={isSaving}
                      />
                    )}
                  />
                }
                label="Active"
              />
            </FormGroup>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider light />
        </Grid>

        <Grid item className={classes.layoutButton} />

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            onClick={() => router.push(paths.index)}
            disabled={isSaving || isLoading}
          >
            Cancel
          </Button>
        </Grid>

        <Grid item className={classes.button}>
          <Button
            variant="contained"
            color="primary"
            disabled={isSaving || isLoading}
            type="submit"
            startIcon={
              isSaving ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                <SaveIcon />
              )
            }
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default CreateForm;
